import React, { useEffect, useState } from 'react'
import shortid from 'shortid'

import { getGlobalParams, getCustomIntl } from 'config/config'
import { updateBreadCrumb, updateHtmlTitle, updateMetaTag } from 'utils/interaction'
import { dispatchFundPage } from 'utils/tracking'
import { syncFilterData, correctAction, mergeSplittingWords } from 'utils/filter'
import { getDividendRecords, getSplittingWords } from '../services'
import { formatDividendAloneTableData } from '../data.format'

import { createFilterStore, getFilterStore, clearFilterStore } from '../../../../data/subjects/filterStoreProducer'
import { dividendSubject, nextByFilter } from '../../../../data/subjects/dividendItems'
import terms from '../terms.json'
import rule from './rule'

import DividendRecords from '../../../templates/DividendRecords'
import HomepageLevelMessage from '../../../atoms/HomepageLevelMessage'

import './style.scss'

const TWDividendRecords = () => {
  const intl = getCustomIntl()
  const { countryConfig } = getGlobalParams()

  const [filterData, setFilterData] = useState()
  const [dividendTableData, setDividendTableData] = useState()
  const [dividendDocumentsData, setDividendDocumentsData] = useState()
  const [dividendRecordsError, setDividendRecordsError] = useState()

  const iconChoices = {
    yes: {
      icon: 'custom-tick',
      color: 'green-400',
      size: 24
    },
    no: {
      icon: 'close',
      color: 'grey-100',
      size: 24
    }
  }

  const monthlyDividendComparisonTable = {
    extraClasses: 'tw-fil-fund-dividend-records-comparison-table',
    tableData: {
      columns: [
        {
          key: '',
          title: '',
          cell: {
            extraClasses: 'text-left',
            render: (data) => {
              const { title } = data
              return <span>{title}</span>
            }
          },
          extraClasses: 'text-left'
        },
        {
          key: 'a-mdist-share-class',
          title: 'A股月配息類股',
          cell: {
            extraClasses: 'text-left',
            render: (data) => {
              const { mdistShare } = data
              return mdistShare.isIcon ? <fil-icon {...iconChoices[mdistShare.iconStatus]} /> : <span>{mdistShare}</span>
            }
          },
          extraClasses: 'text-left'
        },
        {
          key: 'a-mincome-share-class',
          title: 'A股穩定月配息類股',
          cell: {
            extraClasses: 'text-left',
            render: (data) => {
              const { mincomeShare } = data
              return mincomeShare.isIcon ? <fil-icon {...iconChoices[mincomeShare.iconStatus]} /> : <span>{mincomeShare}</span>
            }
          },
          extraClasses: 'text-left'
        },
        {
          key: 'a-mincome-g-share-class',
          title: 'A股 F1穩定月配息類股',
          cell: {
            extraClasses: 'text-left',
            render: (data) => {
              const { mincomeGShare } = data
              return mincomeGShare.isIcon ? <fil-icon {...iconChoices[mincomeGShare.iconStatus]} /> : <span>{mincomeGShare}</span>
            }
          },
          extraClasses: 'text-left'
        },
        {
          key: 'a-hmdist-g-hedged-share-class',
          title: 'A股H月配息貨幣避險類股',
          cell: {
            extraClasses: 'text-left',
            render: (data) => {
              const { hmdistGHedgedShare } = data
              return hmdistGHedgedShare.isIcon ? (
                <fil-icon {...iconChoices[hmdistGHedgedShare.iconStatus]} />
              ) : (
                <span>{hmdistGHedgedShare}</span>
              )
            }
          },
          extraClasses: 'text-left'
        },
        {
          key: 'a-mcdist-g-share-class',
          title: 'A股C月配息類股',
          cell: {
            extraClasses: 'text-left',
            render: (data) => {
              const { mcdistGShare } = data
              return mcdistGShare.isIcon ? <fil-icon {...iconChoices[mcdistGShare.iconStatus]} /> : <span>{mcdistGShare}</span>
            }
          },
          extraClasses: 'text-left'
        }
      ],
      dataSource: countryConfig.monthlyDividendComparisonTable.body
    }
  }

  useEffect(async () => {
    // Call Django function to update breadcrumb
    updateBreadCrumb(intl.formatMessage({ id: 'allDividendInfo.title' }))
    updateHtmlTitle(intl.formatMessage({ id: 'allDividendInfo.title' }))
    updateMetaTag([
      {
        attrName: 'name',
        attrContent: 'title',
        content: `${intl.formatMessage({ id: 'allDividendInfo.title' })}${intl.formatMessage({ id: 'titleSuffix' })}`
      }
    ])
    dispatchFundPage()
  }, [])

  useEffect(async () => {
    dividendSubject.subscribe((items) => {
      const formattedTableData = formatDividendAloneTableData(items)
      setDividendTableData(formattedTableData)
    })

    const fullDividendRecordsPromise = getDividendRecords()
    const splittingWordsPromise = getSplittingWords()

    const [fullDividendRecords, splittingWords] = await Promise.all([fullDividendRecordsPromise, splittingWordsPromise])

    if (!fullDividendRecords) {
      setDividendRecordsError(true)
    } else {
      const { data: dividendRecordsData, documents } = fullDividendRecords
      setDividendDocumentsData(documents)
      // init dividend filter data
      const fixedRule = { ...rule, filter: mergeSplittingWords(rule.filter, splittingWords) }
      const dividendFilterData = createFilterStore('DIVIDEND', { fundItems: dividendRecordsData, rule: fixedRule, terms })
        .updateKeyOrderFromUrl(window.location.href)
        .calcFilterData()
      const finalDividendFilterData = syncFilterData(dividendFilterData)
      setFilterData(finalDividendFilterData)
      nextByFilter(dividendRecordsData, finalDividendFilterData)
    }

    return () => {
      dividendSubject.unsubscribe()
      clearFilterStore('DIVIDEND')
    }
  }, [])

  const updateFilterData = (_action) => {
    const action = correctAction(_action)
    const filterStore = getFilterStore('DIVIDEND')
    const newState = filterStore.transferState(action)
    setFilterData(newState.filterData)
    nextByFilter(filterStore.getFundItems(), newState.filterData)
  }

  return (
    <React.Fragment>
      {dividendRecordsError ? (
        <HomepageLevelMessage type="dividend" monthlyDividendComparisonTable={monthlyDividendComparisonTable} />
      ) : (
        <DividendRecords
          className="tw-fil-fund-dividend-records"
          id={`tw-fil-fund-dividend-records-${shortid.generate()}`}
          {...{
            allDividendData: dividendTableData,
            dividendDropdownMenu: dividendDocumentsData,
            monthlyDividendComparisonTable,
            dividendIntroDialogContent: intl.formatMessage({ id: 'allDividendInfo.dividendIntroDialogContent' }),
            filterData,
            updateFilterData,
            disclaimer: intl.formatMessage({ id: 'allDividendInfo.wholePageDisclaimer' })
          }}
        />
      )}
    </React.Fragment>
  )
}

TWDividendRecords.propTypes = {}

TWDividendRecords.defaultProps = {}

export default TWDividendRecords
